import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { IAccountUserProps, IUser } from '@src/types/user.types'
import userApi from '@src/api/user'
import accountsApi from '@src/api/accounts'
import {
  getFullName, getPermissionLevel, isSuperAdmin, isContentAdmin
} from '@src/utilities/userUtils'
import { initLanguage } from '@src/plugins/i18n'
import { timeDiff } from '@src/utilities/date'
import { userGraphQlApi } from '@src/api/apiModuleInstances'
import _isEmpty from 'lodash/isEmpty'

export function getIsNewUserWithinDays(first_seen: string | null | undefined, days: number) {
  return !first_seen || timeDiff(first_seen, new Date().toISOString(), 'days') < days
}

export const useUserStore = defineStore('user', () => {
  const user = ref<IUser>({} as IUser)
  const accountUserProps = ref<IAccountUserProps>({ socialLinks: [] })
  const loading = ref(true)
  const error = ref(false)
  const imageError = ref(false)
  const roles = ref<string[]>([])

  const userFullName = computed(() => getFullName(user.value.firstname, user.value.lastname))
  const isNewUserSevenDays = computed(() => getIsNewUserWithinDays(user.value.first_seen, 7))
  const isNewUserThirtyDays = computed(() => getIsNewUserWithinDays(user.value.first_seen, 30))
  const permissionLevel = computed(() => getPermissionLevel(roles.value))
  const userIsSuperAdmin = computed(() => isSuperAdmin(roles.value))
  const userIsContentAdmin = computed(() => isContentAdmin(roles.value))
  const userId = computed(() => user.value.id)

  async function loadCurrentUser() {
    loading.value = true
    error.value = false

    try {
      const { data: userData } = await userApi.throttled.getCurrentUser()
      user.value = userData
      initLanguage(userData.language)
    } catch {
      error.value = true
    }

    try {
      const { data: rolesData } = await userApi.cached.getRoles()
      if (_isEmpty(rolesData)) throw new Error('Could not get roles')
      roles.value = rolesData
    } catch {
      // ignore
    }

    loading.value = false
  }

  async function setUserImage(file: File) {
    imageError.value = false

    try {
      const { data } = await accountsApi.setAccountImage(user.value, file)
      user.value = {
        ...user.value,
        image: data.url
      }
    } catch {
      imageError.value = true
    }
  }

  async function deleteUserImage() {
    imageError.value = false

    try {
      const { data } = await accountsApi.deleteAccountImage(user.value)
      user.value = {
        ...user.value,
        image: data.url
      }
    } catch {
      imageError.value = true
    }
  }

  async function saveUserPartialUpdate(updatedUser: Partial<IUser>) {
    error.value = false

    try {
      await userGraphQlApi.saveUser({
        global_user_id: user.value.global_user_id,
        ...updatedUser
      })
      user.value = { ...user.value, ...updatedUser }
      return true
    } catch {
      error.value = true
      return false
    }
  }

  return {
    user,
    userId,
    accountUserProps,
    loading,
    error,
    imageError,
    roles,
    userFullName,
    isNewUserSevenDays,
    isNewUserThirtyDays,
    permissionLevel,
    isSuperAdmin: userIsSuperAdmin,
    isContentAdmin: userIsContentAdmin,
    loadCurrentUser,
    setUserImage,
    deleteUserImage,
    saveUserPartialUpdate
  }
})
